import type { WordEntry } from "@huosu/shared";
import { conciseStudyMeaning, contextualConstruction, contextualName, translationSense, type StudyContext } from './study-context.js';

export type StudyDictionaryEntry = Pick<WordEntry, "word" | "usPhonetic" | "ukPhonetic" | "meanings" | "forms">;
export const studyKey = (word: string) => word.toLowerCase().replace(/[‘’]/g, "'").trim();
type Sense = { pos: string; zh: string };
const posName: Record<string, string> = { n: "名词", v: "动词", adj: "形容词", adv: "副词", pron: "人称代词", prep: "介词", conj: "连词", art: "冠词", aux: "助动词", modal: "情态动词", det: "物主限定词", inf: "不定式标记", num: "数词", int: "感叹词", proper: "专有名词", abbr: "缩写" };
const posColors: Record<string, string> = { n: "#e84591", v: "#8555eb", adj: "#00a67a", adv: "#159fdb", pron: "#c53ce5", prep: "#e58a00", conj: "#ef7614", art: "#08a6c6", aux: "#779c14", modal: "#779c14", det: "#08a6c6" };
const normalizePos = (pos: string) => ({ a: "adj", vt: "v", vi: "v", "v.t": "v", "v.i": "v" }[pos.replace(/\.$/, "")] || pos.replace(/\.$/, ""));

// Imported entries may aggregate multiple POS in one translation and mislabel
// meanings[0].partOfSpeech. Parse the dictionary's own sense markers instead.
export function dictionarySenses(entry?: StudyDictionaryEntry): Sense[] {
  if (!entry) return [];
  return entry.meanings.flatMap((meaning) => {
    const text = meaning.zh.replace(/\\n/g, "\n");
    const markers = [...text.matchAll(/(?:^|[\n；;])\s*(abbr|interj|pron|prep|conj|modal|aux|adj|adv|art|num|int|pl|vt|vi|n|v|a)\.\s*/g)];
    if (!markers.length) return [{ pos: normalizePos(meaning.partOfSpeech), zh: text }];
    return markers.map((match, index) => ({
      pos: normalizePos(match[1] === 'pl' ? 'n' : match[1] === 'interj' ? 'int' : match[1]),
      zh: text.slice(match.index! + match[0].length, markers[index + 1]?.index ?? text.length).split(/\n|\[医\]|\[计\]|\[法\]/)[0].trim()
    }));
  }).filter((sense) => sense.zh);
}

export function formatStudyIpa(raw: string): string {
  const value = raw.trim().replace(/^\/+|\/+$/g, "").replace(/ә/g, "ə").replace(/є/g, "e").replace(/'/g, "ˈ").replace(/:/g, "ː")
    .replace(/ei/g, "eɪ").replace(/ai/g, "aɪ").replace(/oi/g, "ɔɪ").replace(/au/g, "aʊ").replace(/əu/g, "əʊ")
    .replace(/i(?!ː)/g, "ɪ").replace(/u(?!ː)/g, "ʊ").replace(/ɒː/g, "ɔː");
  return value ? `/${value}/` : "";
}

// A sentence transcription is only usable when its boundaries match exactly.
// Never move extra/missing IPA syllables onto the next word.
export function alignedSentenceIpa(raw: string, count: number): string[] {
  const parts = raw.trim().replace(/^\/+|\/+$/g, "").split(/\s+/).filter(Boolean);
  return parts.length === count ? parts.map(formatStudyIpa) : [];
}

function regularPluralIpa(entry?: StudyDictionaryEntry, lemma?: StudyDictionaryEntry) {
  if (!entry || !lemma || !/[s3]/.test(entry.forms?.["1"] || "")) return "";
  const base = studyKey(lemma.word);
  const surface = studyKey(entry.word);
  if (![`${base}s`, `${base}es`, ...(base.endsWith("y") ? [`${base.slice(0, -1)}ies`] : [])].includes(surface)) return "";
  const ipa = formatStudyIpa(lemma.usPhonetic || lemma.ukPhonetic).replace(/^\/|\/$/g, "");
  if (!ipa) return "";
  const suffix = /[szʃʒ]$/.test(ipa) ? "ɪz" : /[ptkfθ]$/.test(ipa) ? "s" : "z";
  return `/${ipa}${suffix}/`;
}

const closedClass: Record<string, [string, string]> = {
  i: ["pron", "我"], you: ["pron", "你；你们"], we: ["pron", "我们"], they: ["pron", "他们"], he: ["pron", "他"], she: ["pron", "她"], it: ["pron", "它"],
  my: ["det", "我的"], your: ["det", "你的；你们的"], our: ["det", "我们的"], their: ["det", "他们的"], its: ["det", "它的"],
  a: ["art", "一个"], an: ["art", "一个"], the: ["art", "这／那（特指）"],
  can: ["modal", "能；可以"], could: ["modal", "能；可以（委婉）"], should: ["modal", "应该"], must: ["modal", "必须"], would: ["modal", "会；愿意"],
  and: ["conj", "和；并且"], or: ["conj", "或者"], but: ["conj", "但是"],
  about: ["prep", "关于"], in: ["prep", "在……里"], on: ["prep", "在……上"], with: ["prep", "和；用"], for: ["prep", "为了；对于"], of: ["prep", "……的"],
};

export function annotateStudyWord(words: string[], index: number, entries: Record<string, StudyDictionaryEntry>, alignedIpa = "", grammarRole = "", context: StudyContext = {}) {
  const word = studyKey(words[index]);
  const entry = entries[word];
  const lemma = entries[studyKey(entry?.forms?.["0"] || "")];
  const senses = dictionarySenses(entry);
  let sense = senses[0] || { pos: "", zh: "" };
  let qualifier = "";
  let ipa = alignedIpa || formatStudyIpa(entry?.usPhonetic || entry?.ukPhonetic || "") || regularPluralIpa(entry, lemma);
  const previous = studyKey(words[index - 1] || "");
  const next = studyKey(words[index + 1] || "");
  const nextSenses = dictionarySenses(entries[next]);
  const form = entry?.forms?.["1"] || "";
  const lemmaSenses = dictionarySenses(lemma);
  // Inflected everyday words such as ideas/maps/onions must not use an
  // unrelated all-caps acronym, film title or surname imported at that spelling.
  if (form.includes('s') && lemma && !grammarRole.includes('谓语') && !/^[A-Z]{2,}$/.test(words[index])) {
    sense = lemmaSenses.find(s=>s.pos==='n') || sense;
  }
  const closed = closedClass[word];
  // Modal can is not the noun in "a can"; similarly preserve noun senses.
  if (closed && !(closed[0] === "modal" && /^(a|an|the|this|that)$/.test(previous))) sense = { pos: closed[0], zh: closed[1] };
  if (word === "to") sense = { pos: nextSenses.some((s) => s.pos === "v") ? "inf" : "prep", zh: nextSenses.some((s) => s.pos === "v") ? "去做……" : "向；到" };
  if (/^(am|is|are|was|were|be|been|being)$/.test(word)) sense = { pos: "v", zh: "是；处于" };
  if (/^(can|could|should|must|would|will|to)$/.test(previous)) sense = senses.find((s) => s.pos === "v") || lemmaSenses.find(s=>s.pos==='v') || sense;
  if (grammarRole.includes("谓语") && sense.pos !== "modal" && !/^(am|is|are|was|were|be|been|being)$/.test(word)) {
    sense = senses.find((s) => s.pos === "v") || dictionarySenses(lemma).find((s) => s.pos === "v") || sense;
    if (sense.pos === "v" && /3|s/.test(form)) qualifier = "动词第三人称单数";
  }
  if (/^(a|an|the|my|your|our|their)$/.test(previous) && senses.some((s) => s.pos === "n") && sense.pos === "v") sense = senses.find((s) => s.pos === "n")!;
  if (form.includes("r") || form.includes("t")) {
    qualifier = form.includes("r") ? "比较级" : "最高级";
    const adjective = dictionarySenses(lemma).find((s) => s.pos === "adj");
    if (adjective) sense = { pos: "adj", zh: `${form.includes("r") ? "更" : "最"}${adjective.zh.split(/[,，；;]/)[0]}` };
  }
  if (form.includes("i")) {
    if (/^(am|is|are|was|were|be|been)$/.test(previous)) {
      sense = { pos: "v", zh: dictionarySenses(lemma).find((s) => s.pos === "v")?.zh || sense.zh };
      qualifier = "现在分词";
    } else if (sense.pos === "n" && senses.some((s) => s.pos === "adj") && !/^(a|an|the|my|your|our|their)$/.test(previous)) {
      qualifier = "动名词";
    }
    // A lexical noun such as "the meeting" keeps its own noun meaning;
    // having an -ing exchange link is not evidence of a progressive verb.
  }
  // learned has two pronunciations: /lɜːnɪd/ (adjective) vs /lɜːnd/ (verb).
  // Use the dictionary's lemma/form evidence and context, not the adjective IPA.
  const attributiveAdjective = /^(a|an|the|very|more|most)$/.test(previous) && nextSenses.some((s) => s.pos === "n");
  if (/[pd]/.test(form) && lemma && !attributiveAdjective && dictionarySenses(lemma).some((s) => s.pos === "v")) {
    sense = dictionarySenses(lemma).find((s) => s.pos === "v")!;
    qualifier = /^(have|has|had|is|are|was|were|been)$/.test(previous) ? "过去分词" : "过去式";
    if (word === "learned" && !alignedIpa) ipa = "/lɜːnd/";
  }
  if (word === "yesterday" && index > 0 && previous !== "of") sense = senses.find((s) => s.pos === "adv") || sense;
  // make + object + adjective is a causative complement, not manufacture.
  const complementIndex = context.roles?.findIndex((role,i)=>i>index && /^(?:宾补|宾语补足语|补语)$/.test(role)) ?? -1;
  const nearbyComplement = complementIndex > index && complementIndex <= index+5 && !words.slice(index+1,complementIndex).some(w=>/^(about|in|on|for|with|after|before|and|but)$/i.test(w));
  const directAdjectiveComplement = !context.roles?.length && dictionarySenses(entries[studyKey(words[index+2]||'')]).some(s=>s.pos==='adj') && !dictionarySenses(entries[studyKey(words[index+3]||'')]).some(s=>s.pos==='n');
  if (/^(make|makes|made|making)$/.test(word) && (nearbyComplement || directAdjectiveComplement)) {
    sense = { pos: "v", zh: "使；让" };
    qualifier = "使役动词";
  } else if (/^(make|makes|made|making)$/.test(word) && /制作|制造/.test(context.chinese || '')) {
    sense = {pos:'v',zh:'制作'};
  }
  if (/^(learn|learns|learned|learnt|learning)$/.test(word) && next === "about") sense = { pos: "v", zh: "了解；学习" };
  if (grammarRole.includes("谓语") && /^(share|shares|shared|sharing)$/.test(word) && sense.zh.includes("分享") && words.slice(index + 1, index + 7).some(text => /^(stories|story|ideas?|news|experiences?|photos?|thoughts?)$/.test(studyKey(text)))) sense = { pos: "v", zh: "分享" };
  const named = contextualName(words,index,sense,context,grammarRole);
  if(named) { sense=named; qualifier='人名'; }
  else {
    sense=translationSense(sense,[sense,...senses,...lemmaSenses],context.chinese||'');
    const construction=contextualConstruction(words,index,context,sense);
    if(construction) { sense=construction; if(construction.pos==='aux'||construction.pos==='proper'||construction.pos==='num')qualifier=''; }
  }
  // A form-only entry (e.g. has: "have 的第三人称单数") is not a definition.
  if (!named && !conciseStudyMeaning(sense.zh)) {
    const baseSense = lemmaSenses.find(s=>s.pos===sense.pos && conciseStudyMeaning(s.zh)) || lemmaSenses.find(s=>conciseStudyMeaning(s.zh));
    if(baseSense) sense=translationSense(baseSense,lemmaSenses,context.chinese||'');
  }
  if (!named && /'s$/.test(word) && !conciseStudyMeaning(sense.zh)) {
    const baseSense=dictionarySenses(entries[word.slice(0,-2)]).find(s=>conciseStudyMeaning(s.zh));
    if(baseSense) sense={pos:'det',zh:conciseStudyMeaning(translationSense(baseSense,[baseSense],context.chinese||'').zh).split('；')[0]+'的'};
  }
  const shortMeaning = named ? named.zh : conciseStudyMeaning(sense.zh);
  return { phonetic: ipa, explain: shortMeaning, partOfSpeech: sense.pos, label: qualifier || posName[sense.pos] || "", color: posColors[sense.pos] || "#60748a" };
}
